import { FONT_TEXT } from './customFonts';

const DOTS_PER_INCH = 96;

export const PORTRAIT_WIDTH = 8.5 * DOTS_PER_INCH;
export const PORTRAIT_HEIGHT = 11 * DOTS_PER_INCH;

export const LANDSCAPE_WIDTH = 11 * DOTS_PER_INCH;
export const LANDSCAPE_HEIGHT = 8.5 * DOTS_PER_INCH;

export const getPageDimensions = (
  orientation: 'portrait' | 'landscape'
): { width: number; height: number } => {
  if (orientation === 'landscape') {
    return { width: LANDSCAPE_WIDTH, height: LANDSCAPE_HEIGHT };
  }
  return { width: PORTRAIT_WIDTH, height: PORTRAIT_HEIGHT };
};

// Gap between stacked sheets, in screen pixels. ScaledPrintableRenderer lays
// its sheets out top to bottom so a multi-page printable is read by scrolling
// (AH-2504). It matches the space the printable action bar leaves above the
// first sheet (Chakra `2`), so the bar and the sheets read as evenly spaced
// parts of one document rather than as separate blocks.
export const SHEET_GAP = 8;

// Breathing room under the last sheet, in screen pixels, so the stack doesn't
// end flush against whatever follows it.
export const SHEET_TRAILING_SPACE = 24;

// Wrapper class for the printable's universal selectors. Scoping protects the
// host app when rendered inline; the iframe also wears it so a single set of
// styles works in both environments. A host-app rule using descendant
// selectors against generic elements (e.g. `body div { ... }`) could still
// reach inside this wrapper — isolation is best-effort, not bulletproof.
export const ISOLATE_CLASS = 'printable-isolate';

// Returns scoped styles safe to inject into any document. Document-level
// concerns (html/body sizing, @page rule) belong to the host environment and
// are intentionally omitted — see frameContent.ts for the iframe additions.
//
// The universal reset is wrapped in :where() so it carries zero specificity —
// otherwise `.scope *` (0,0,1,1) would clobber every Chakra style-prop padding
// and margin we set on descendants, since Chakra's generated classes only
// score (0,0,1,0). With :where(), the reset acts as a defaults layer that any
// component-level style prop can override.
//
// The scope declares its own base line height (unitless, so it inherits as a
// factor of each descendant's font size) rather than taking the host document's,
// because the two rendering environments don't have the same host. Inline
// (ScaledPrintableRenderer) the app's ChakraProvider puts lineHeights.base — 1.4
// — on <body> and the sheet inherited it; in the print iframe
// PrintableChakraProvider passes disableGlobalStyle, so nothing set it and the
// sheet fell back to the UA's 'normal' (~1.28 for DM Sans). Every line that
// doesn't set its own line height was therefore shorter on paper than in the
// preview, and since pagination measures in whichever environment it runs, the
// same list fit one more event per page in the iframe than on screen (AH-2508).
export const getPrintableStyles = (scope: string) => `
  :where(.${scope}) * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .${scope} {
    font-family: ${FONT_TEXT};
    font-size: 16px;
    line-height: 1.4;
  }

  :where(.${scope} .page) {
    position: relative;
    height: 100%;
    width: 100%;
    padding: 1.25em;
  }

  .${scope} .twemoji {
    height: 1em;
    width: 1em;
    margin: 0 0.05em 0 0.1em;
    vertical-align: -0.1em;
    display: inline-block;
  }

  .${scope} h1 .twemoji,
  .${scope} h2 .twemoji,
  .${scope} h3 .twemoji {
    height: 0.9em;
    width: 0.9em;
    vertical-align: -0.1em;
  }

  .${scope} .footer .twemoji {
    height: 1.2em;
    width: 1.2em;
    vertical-align: -0.2em;
  }

  @media print {
    .${scope} {
      width: 100%;
      height: 100%;
    }

    .${scope} img {
      max-width: 100%;
      page-break-inside: avoid;
    }

    .${scope} .page {
      display: block !important;
      page-break-after: always;
      page-break-inside: avoid;
    }

    .${scope} .page:last-child {
      page-break-after: auto;
    }

    .${scope} .twemoji {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
      page-break-inside: avoid;
    }
  }

  @media screen {
    .${scope} .page {
      display: none;
    }
    .${scope} .page[data-current-page="true"] {
      display: block;
    }
  }

  @media screen and (max-width: 768px) {
    .${scope} .twemoji {
      height: 1em;
      width: 1em;
    }
  }
`;
